<script setup lang="ts">
  defineOptions({
    name: 'AuthPageLayoutWeb',
  })

  import { computed, ref, watch, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { ArrowLeftIcon, Mail01Icon } from '@gohighlevel/ghl-icons/24/outline'
  import CPText from '@ui/components/CPText.vue'

  import AuthBrandingHeader from '@common/auth-layer/components/AuthBrandingHeader.vue'

  import AuthHandler from '@common/auth-layer/components/AuthHandler.vue'

  import { Screens, type ScreenChangeData } from '@common/auth-layer/types'

  import { createElementId } from '@common/core-layer/utils'

  import { useLocation } from '@common/location-layer/composables/useLocation'

  const props = defineProps({
    initialPage: {
      type: String as PropType<string>,
      default: Screens.LOGIN,
    },
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: undefined,
    },
    isSSODisabled: {
      type: Boolean,
      default: undefined,
    },
    /**
     * Clientportal: defer OTP/Google session until after in-place Set Password.
     * Default true for `/login`. Set false on `/auth` credential processor so Set Password completion stays in the page callback (single saveAuthAfterLogin).
     */
    deferSetPasswordSession: {
      type: Boolean,
      default: true,
    },
  })

  const emit = defineEmits<{
    (e: 'loginCallback', data: unknown): void
    (e: 'changeScreen', data: ScreenChangeData): void
  }>()

  const handleLoginCallback = (event: unknown) => {
    emit('loginCallback', event)
  }

  const { t } = useI18n()

  const { store: locationStore } = useLocation()

  // Track current screen and email so header and AuthHandler stay in sync (matches LoginModal behaviour)
  const currentScreen = ref(props.initialPage)

  const currentUserEmail = ref('')

  // Branding data from location store
  const portalLogo = computed(() => locationStore.branding?.portalImage || locationStore.branding?.logo || null)

  const portalName = computed(() => locationStore.branding.portalBannerText || t('auth.branding.defaultPortalName'))

  const resolvedIsSSODisabled = computed(() => {
    if (props.isSSODisabled !== undefined) return props.isSSODisabled
    return locationStore.locationData?.isSSODisabled === true
  })

  const handleChangeScreen = (data: ScreenChangeData) => {
    if (data.page) {
      currentScreen.value = data.page
      currentUserEmail.value = data.email || ''
    }
    emit('changeScreen', data)
  }

  const handleBack = (targetPage: string, email?: string) => {
    handleChangeScreen({ page: targetPage, email: email || currentUserEmail.value })
  }

  const showBrandingLeadingRow = computed(
    () =>
      currentScreen.value === Screens.LOGIN_WITH_SECURE_CODE ||
      currentScreen.value === Screens.FORGOT_PASSWORD ||
      currentScreen.value === Screens.VERIFY_SECURE_CODE
  )

  const embedsBrandingInChildFlow = computed(
    () => currentScreen.value === Screens.EXPIRED_PASSWORD || currentScreen.value === Screens.PROFILE_CONFIRMATION
  )

  watch(
    () => props.initialPage,
    newPage => {
      if (newPage) currentScreen.value = newPage
    }
  )
</script>

<template>
  <div class="flex w-full flex-col">
    <AuthBrandingHeader v-if="!embedsBrandingInChildFlow" :show-branding="true" :portal-logo="portalLogo" :portal-name="portalName">
      <template v-if="showBrandingLeadingRow" #leading>
        <div
          v-if="currentScreen === Screens.LOGIN_WITH_SECURE_CODE"
          :id="createElementId('auth-page-layout', 'secure-code', 'back-button')"
          @click="handleBack(Screens.LOGIN)"
        >
          <ArrowLeftIcon class="h-5 w-5 flex-shrink-0 cursor-pointer rounded-full hover:bg-hover" />
        </div>
        <div
          v-else-if="currentScreen === Screens.FORGOT_PASSWORD"
          :id="createElementId('auth-page-layout', 'forgot-password', 'back-button')"
          @click="handleBack(Screens.LOGIN)"
        >
          <ArrowLeftIcon class="h-5 w-5 flex-shrink-0 cursor-pointer rounded-full hover:bg-hover" />
        </div>
        <div
          v-else-if="currentScreen === Screens.VERIFY_SECURE_CODE"
          :id="createElementId('auth-page-layout', 'verify-code', 'back-button')"
          @click="handleBack(Screens.LOGIN_WITH_SECURE_CODE, currentUserEmail)"
        >
          <ArrowLeftIcon class="h-5 w-5 flex-shrink-0 cursor-pointer rounded-full hover:bg-hover" />
        </div>
      </template>
    </AuthBrandingHeader>

    <div v-if="currentScreen === Screens.LOGIN_WITH_SECURE_CODE" class="mb-4 w-full">
      <CPText size="xl" weight="semibold" class="text-body">
        {{ t('auth.loginModal.enterEmailToLogin') }}
      </CPText>
    </div>
    <div v-else-if="currentScreen === Screens.FORGOT_PASSWORD" class="mb-4 w-full">
      <CPText size="xl" weight="semibold" class="text-body">
        {{ t('auth.loginModal.resetYourPassword') }}
      </CPText>
    </div>
    <div v-else-if="currentScreen === Screens.VERIFY_SECURE_CODE" class="mb-4 flex w-full min-w-0 items-center gap-2">
      <Mail01Icon class="h-6 w-6 flex-shrink-0" />
      <div class="flex min-w-0 flex-1 flex-col">
        <CPText size="xl" weight="semibold" class="text-body">
          {{ t('auth.loginModal.verifySecureCode') }}
        </CPText>
        <CPText size="sm" weight="regular" class="truncate text-secondary">{{ t('auth.loginModal.sentTo') }} {{ currentUserEmail }}</CPText>
      </div>
    </div>

    <AuthHandler
      :page="currentScreen"
      :referrer-user="referrerUser"
      :show-branding="true"
      :portal-logo="portalLogo"
      :portal-name="portalName"
      :is-sso-disabled="resolvedIsSSODisabled"
      :defer-set-password-session="deferSetPasswordSession"
      @login-callback="handleLoginCallback"
      @change-screen="handleChangeScreen"
    />
  </div>
</template>

<style scoped></style>
